import { Injectable } from "@angular/core";
import { Observable } from "rxjs";

export interface SseTextEvent {
  kind: "text";
  data: string;
}
export interface SseSourcesEvent {
  kind: "sources";
  data: { title: string; uri: string }[];
}

export interface SseSearchHtmlEvent {
  kind: "search_html";
  data: string;
}

export interface SseFollowupsEvent {
  kind: "followups";
  data: string[];
}

export interface SseErrorEvent {
  kind: "error";
  data: string;
}

export type SseEvent = SseErrorEvent | SseFollowupsEvent | SseSearchHtmlEvent | SseSourcesEvent | SseTextEvent;

@Injectable({
  providedIn: "root",
})
export class XhrSourceService {
  private parseSseEvent(message: string): MessageEvent<unknown> {
    // Restore literal new-lines
    message = message.replace(/\\n/g, "\n");

    // Split header / payload
    let type = "message";
    let start = 0;
    if (message.startsWith("event: ")) {
      start = message.indexOf("\n");
      type = message.slice(7, start).trim();
    }
    start = message.indexOf(": ", start) + 2;
    const raw = message.slice(start);

    // Strong runtime checking
    let data: unknown = raw; // default → plain text

    // Grounding_sources to JSON array of { title, uri }
    if (type === "grounding_sources") {
      try {
        const parsed: unknown = JSON.parse(raw);
        if (
          Array.isArray(parsed)
          && parsed.every(
            (e) =>
              typeof (e as { title?: unknown }).title === "string" && typeof (e as { uri?: unknown }).uri === "string",
          )
        ) {
          data = parsed as { title: string; uri: string }[];
        }
      } catch {
        // ignore malformed JSON – fall back to raw string
        console.warn("Ignoring malformed grounding_sources JSON", raw);
      }
    }

    // Search_entry_html to raw HTML snippet
    else if (type === "search_entry_html") {
      data = raw;
    }
    // Follow_up_queries to JSON array of strings
    else if (type === "follow_up_queries") {
      try {
        const parsed: unknown = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.every((q) => typeof q === "string")) {
          data = parsed;
        }
      } catch {
        // Ignore malformed JSON – fall back to raw string
        console.warn("Ignoring malformed follow_up_queries JSON", raw);
      }
    }

    return new MessageEvent(type, { data });
  }

  createXhrSource(
    url: string,
    opts: {
      method?: string;
      headers?: Record<string, string>;
      timeout?: number;
      body?: Document | XMLHttpRequestBodyInit | null;
    },
  ): Observable<MessageEvent> {
    return new Observable<MessageEvent>((observer) => {
      const xhr = new XMLHttpRequest();
      xhr.open(opts.method || "GET", url, true);
      if (opts.headers) {
        for (const k in opts.headers) {
          if (Object.hasOwn(opts.headers, k)) {
            xhr.setRequestHeader(k, opts.headers[k]!);
          }
        }
      }

      let ongoing = false;
      let start = 0;

      xhr.onprogress = () => {
        if (!ongoing) {
          ongoing = true;
          const openEvent = new MessageEvent("open", {
            data: {
              status: xhr.status,
              headers: xhr.getAllResponseHeaders(),
              url: xhr.responseURL,
            },
          });
          observer.next(openEvent);
        }

        let i: number;
        let chunk: string;

        while ((i = xhr.responseText.indexOf("\n\n", start)) >= 0) {
          chunk = xhr.responseText.slice(start, i);
          start = i + 2;
          if (chunk.length) {
            const messageEvent = this.parseSseEvent(chunk);
            if (messageEvent.type === "error") {
              observer.error(messageEvent);
            } else {
              observer.next(messageEvent);
            }
          }
        }
      };

      xhr.onloadend = () => {
        if (xhr.status === 200) {
          const closeEvent = new MessageEvent("close");
          observer.next(closeEvent);
          observer.complete();
        } else {
          const errorEvent = new MessageEvent("error", {
            data: `Unexpected error ${xhr.responseText}`,
          });
          observer.error(errorEvent);
        }
      };

      if (opts.timeout) {
        xhr.timeout = opts.timeout;
      }
      xhr.ontimeout = () => {
        const errorEvent = new MessageEvent("error", {
          data: `Network request timed out ${xhr.responseText}`,
        });
        observer.error(errorEvent);
      };
      xhr.onerror = () => {
        const errorEvent = new MessageEvent("error", {
          data: `Network request failed ${xhr.responseText}`,
        });
        observer.error(errorEvent);
      };
      xhr.onabort = () => {
        const errorEvent = new MessageEvent("error", {
          data: `Network request aborted ${xhr.responseText}`,
        });
        observer.error(errorEvent);
      };
      try {
        xhr.send(opts.body || null);
      } catch (err: unknown) {
        const errorEvent = new MessageEvent("error", {
          data: JSON.stringify(err),
        });
        observer.error(errorEvent);
      }

      return () => {
        xhr.abort();
      };
    });
  }
}
